import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl';
import turfBbox from '@turf/bbox';
// Aliased so the bare name does not shadow the global Map constructor.
import { Map as ReconcileMap } from 'components/map';
import { WellMapLayer } from 'components/well-map-layer';
import { unionBounds, type BBox } from 'components/query-map';
import type { WellboreMapData } from 'types/api-responses';
import { reconcileMapConfigAtom, reconcileMapRefAtom } from '../../atoms';
import type { WellCandidate } from './consolidation-utils';
import styles from './reconcile-wells-map.module.scss';

/** Trajectory paint, viewed well first. Map layers take literal colours. */
const TRAJECTORY_COLORS = ['#0f62fe', '#d02670', '#007d79', '#8a3ffc'];

/** Bounds padding in degrees — a single pad's extent is otherwise near zero. */
const FRAMING_PAD_DEGREES = 0.0015;
/** Cap on the fit zoom. High enough that a tight duplicate cluster (near-identical coordinates, so a
 *  near-zero bbox) frames in close and the bores draw as sticks, not dots; it only binds when the
 *  natural fit would exceed it, so a spread-out cluster is unaffected. Kept at 15 (matching
 *  auto-fit-map) rather than higher, so a tight cluster does not over-zoom the basemap tiles. */
const FIT_MAX_ZOOM = 15;
const FIT_PADDING = 32;

interface Camera {
  latitude: number;
  longitude: number;
  zoom: number;
}

/**
 * Widens a bounding box so a single pad, whose extent is near zero, still frames.
 * @param {BBox} bounds The bounds to widen.
 * @return {BBox} The padded bounds.
 */
const padForFraming = ([minLng, minLat, maxLng, maxLat]: BBox): BBox => [
  minLng - FRAMING_PAD_DEGREES,
  minLat - FRAMING_PAD_DEGREES,
  maxLng + FRAMING_PAD_DEGREES,
  maxLat + FRAMING_PAD_DEGREES,
];

/**
 * Keeps the canvas sized to its container.
 *
 * The sheet animates in, so the map's first measurement is taken against a box that is
 * still growing and the canvas paints short of the container's left edge. Mapbox sizes
 * its own container to the canvas, so watching that reports nothing — the element to
 * watch is the wrapper the sheet is actually resizing, and the load event has to
 * re-measure too in case the animation finished before the map was ready.
 * @param {object} props The wrapper element to watch.
 * @return {null} Nothing; this is a behaviour-only child.
 */
function KeepMapSized({
  container,
}: {
  container: React.RefObject<HTMLDivElement>;
}) {
  const { current: map } = useMap();

  useEffect(() => {
    const element = container.current;
    if (!map || !element) return undefined;

    /** Re-measures the canvas against its container. */
    const resize = () => map.resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    map.on('load', resize);
    // One more after the current frame: the observer does not fire when the element was
    // already at its final size before the map finished mounting.
    const frame = requestAnimationFrame(resize);

    return () => {
      observer.disconnect();
      map.off('load', resize);
      cancelAnimationFrame(frame);
    };
  }, [map, container]);

  return null;
}

/**
 * Reports the camera that frames the given bounds, using Mapbox's own solver so the
 * real canvas size is accounted for.
 * @param {object} props The bounds to frame and the callback receiving the camera.
 * @return {null} Nothing; this is a behaviour-only child.
 */
function ReportFitCamera({
  bounds,
  onCamera,
}: {
  bounds: BBox;
  onCamera: (camera: Camera) => void;
}) {
  const { current: map } = useMap();
  const signature = bounds.join(',');

  useEffect(() => {
    if (!map) return undefined;

    /** Solves for the camera framing `bounds` and hands it up. */
    const report = () => {
      const camera = map.cameraForBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM }
      );
      if (!camera?.center || camera.zoom === undefined) return;
      const { lng, lat } = camera.center;
      onCamera({ latitude: lat, longitude: lng, zoom: camera.zoom });
    };

    report();
    map.on('resize', report);
    return () => {
      map.off('resize', report);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `bounds` is a fresh array each render; `signature` is its value, which is what should drive the effect
  }, [map, signature, onCamera]);

  return null;
}

export interface ReconcileWellsMapProps {
  /** The record pool, viewed well first — its order fixes the trajectory colours. */
  pool: WellCandidate[];
  /** Wellbore geometry per row id. Records without an entry simply are not drawn. */
  geometry: Record<string, WellboreMapData>;
}

/**
 * The candidate wellbores drawn together, so two records that claim to be the same well
 * can be judged on where they actually go.
 *
 * As in the mock, the map is not wired to row selection or hover: it draws the whole
 * pool. What is being compared is the set, not whichever row the pointer is over.
 * @param {ReconcileWellsMapProps} props The record pool and its geometry.
 * @return {JSX.Element | null} The map, or nothing when no record has geometry.
 */
export function ReconcileWellsMap({ pool, geometry }: ReconcileWellsMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [camera, setCamera] = useState<Camera | null>(null);
  const onCamera = useCallback((next: Camera) => setCamera(next), []);

  const drawn = useMemo(
    () =>
      pool
        .map((well, index) => ({
          rowId: well.rowId,
          color: TRAJECTORY_COLORS[index % TRAJECTORY_COLORS.length],
          wellbore: geometry[well.rowId],
        }))
        .filter((entry) => Boolean(entry.wellbore)),
    [pool, geometry]
  );

  const fitBounds = useMemo(() => {
    const boxes = drawn
      .map(({ wellbore }) => wellbore.stick ?? wellbore.surfaceHole)
      .filter(Boolean)
      .map((shape) => turfBbox(shape as GeoJSON.GeoJSON) as BBox);
    const union = unionBounds(boxes);
    return union ? padForFraming(union) : null;
  }, [drawn]);

  if (drawn.length === 0 || !fitBounds) return null;

  return (
    <div className={styles.canvas} ref={containerRef}>
      <ReconcileMap
        latitude={camera?.latitude ?? (fitBounds[1] + fitBounds[3]) / 2}
        longitude={camera?.longitude ?? (fitBounds[0] + fitBounds[2]) / 2}
        zoom={camera?.zoom}
        className={styles.map}
        mapConfigAtom={reconcileMapConfigAtom}
        refAtom={reconcileMapRefAtom}
        // `showControls` is what makes `Map` apply its interaction settings at all —
        // without it `scrollZoom: false` never reaches Mapbox and the inset eats the
        // sheet's wheel.
        showControls
        hideMeasureAndPricing
        blockPopups
      >
        <KeepMapSized container={containerRef} />
        <ReportFitCamera bounds={fitBounds} onCamera={onCamera} />
        {drawn.map(({ rowId, color, wellbore }) => (
          <WellMapLayer
            key={rowId}
            id={`reconcile-well-${rowId}`}
            wellbore={wellbore}
            mapConfigAtom={reconcileMapConfigAtom}
            colorOverride={color}
          />
        ))}
      </ReconcileMap>
    </div>
  );
}
